/**
 * Core query-param parsing for the widget shell (P3-4 / MR-32).
 *
 * These helpers are extracted verbatim from app.vue's `extractQueryParams`,
 * `processQueryParams`, and `createStationInformation`. The public embedding
 * contract is BYTE-FROZEN, so the extraction preserves the exact Vue1 output
 * for every input. Pulling them out as pure functions lets the fiddly
 * edge-case behavior be unit-tested without mounting a Vue component.
 *
 * The ONE deliberate deviation from Vue1: `parseQueryString` catches a
 * URIError from malformed %-encoding and falls back to the raw value, where
 * Vue1 let the error propagate out of the boot and crash the widget (hunter #2).
 */

import { pyBool } from './pyBool';

/**
 * Parse a raw query string (the part after `?`, no leading `?`) into a flat
 * params object.
 *
 * Byte-identical to Vue1's hand parser:
 *   - `const [k, v] = s.split('=')` — for `k=a=b` this keeps only the segment
 *     between the 1st and 2nd `=` and DROPS the tail (v === 'a'), and for a
 *     bare key (`?flag`) v is undefined → `decodeURIComponent(undefined)` is
 *     the string 'undefined'. Both are preserved on purpose.
 *   - `Object.assign({}, ...)` — duplicate keys are LAST-wins.
 *   - `decodeURIComponent` does NOT turn `+` into a space (a URLSearchParams
 *     rewrite would, silently changing the contract — so we keep the hand
 *     parser).
 *
 * @param {string} qs raw query string (no leading `?`)
 * @returns {Object<string,string>} parsed params
 */
export const parseQueryString = (qs) =>
  Object.assign(
    {},
    ...qs.split('&').map((s) => {
      const [k, v] = s.split('=');
      let decoded;
      try {
        decoded = decodeURIComponent(v);
      } catch (e) {
        // Malformed %-encoding (e.g. an unencoded `100%`). Vue1 threw a
        // URIError out of the boot here; fall back to the raw value instead
        // of crashing the boot (hunter #2).
        decoded = v;
      }
      return { [k]: decoded };
    })
  );

/**
 * Derive the core shell config from an already-parsed params object.
 *
 * Every field mirrors app.vue exactly — in particular `Number(version) || 1`
 * (absent/0/NaN all default to 1; do NOT "fix" to 4) and `smc` falling back to
 * `zone`.
 *
 * 🪤 THERE IS NO `useM4a` HERE, AND ITS ABSENCE IS DELIBERATE (D-220, decided
 * 2026-09-06). Every revision of this function up to `!366` parsed `use_m4a` — the
 * last of them through `pyBool`, for one boolean grammar across the module. The value
 * was DEAD DOWNSTREAM and had been since `1a61585` (2020-02-21, "M4a for compact"): it
 * reached `app.vue`'s `useM4a` data property and from there only `v2Player.useM4a`,
 * which nothing read, because both mount points in `app.vue`'s template hardcode the
 * prop — `<podcast-list :use-m4a="true">` and `<v2-player :use-m4a="true">`. So v1 and
 * v2 played m4a unconditionally, `use_m4a=false` included, and v3/v4 never consulted it
 * at all — they discriminate on `canPlayLiveStream`. MEASURED by `!366` 2026-09-02 on
 * the built artifact through the real loader: all six of {v1,v2} x {absent, True, false}
 * play the same `.m4a` source. The parse is deleted rather than wired because wiring it
 * would be a live behaviour change nobody asked for.
 *
 * 🔴 IF YOU BRING IT BACK, YOU MUST ALSO ADD `use_m4a` TO `EMBED_ONLY_PARAMS` IN
 * `src/classes/widget.js` — one decision, not two. That guard lists only
 * `{full, vertical, id}`, so a host page's `?use_m4a=false` passes through to the
 * iframe today. That is harmless exactly while nothing here reads it, and the instant
 * something does, one query parameter on a customer's own page silently repoints audio
 * transport for every embed on that page. The rule is executable rather than advisory:
 * `embed-config-precedence.spec.js`'s D-220 leg pins the two halves EQUAL, so a
 * half-move reds and a whole move does not.
 *
 * 🪤 THE PROP ITSELF IS NOT DEAD and must not be deleted with the parse: `V2Player`,
 * `NowPlayingSection`, `podcast.vue` and `podcast_list.vue` — FOUR components — declare
 * a `useM4a` PROP, and it genuinely gates the m4a-vs-HLS source. It is the hardcoded
 * `true` (`app.vue` binds `:use-m4a="true"` on BOTH the `<v2-player>` and the
 * `<podcast-list>` mount) that reaches them, and that is the behaviour being preserved.
 *
 * 🪤 `LiveAudioPlayer` IS NOT ON THAT LIST — it declares no such prop. Its `useM4a` is a
 * LOCAL COMPUTED, `audioFileSrc.includes('m4a')`, which is FALSE on the live stream. Do
 * not constant-fold its `this.useM4a` branches to the m4a side on the strength of the
 * hardcoded prop above: that sets a live `.m3u8` as `src` with no hls.js attached and
 * reintroduces D-165/D-148.
 *
 * P6-5 (MR-54) adds the FORCE_V4 clamp: when `options.forceV4` is `true`, a
 * legacy requested version (anything but 4) resolves to `version: 4` so the
 * shell renders the v4 player for every embed. It is OFF unless `forceV4 === true`
 * (the exact boolean — a truthy string/number does NOT enable it), so the default
 * single-argument call preserves the byte-frozen contract. `requestedVersion`
 * always carries the ORIGINAL pre-clamp version (P6-17 reads it via telemetry).
 *
 * @param {Object<string,string>} queryParams parsed query params
 * @param {{forceV4?: boolean}} [options] `forceV4` clamps legacy → v4 (default OFF)
 * @returns {Object} core config (adds `requestedVersion`, the pre-clamp version)
 */
export const parseCoreConfig = (queryParams, options) => {
  const forceV4 = !!options && options.forceV4 === true;
  const zone = queryParams.zone;
  const next = queryParams.next || 'none';
  // The version the embed actually requested, pre-clamp — `Number(version) || 1`
  // verbatim (absent/0/NaN → 1; do NOT "fix" to 4).
  const requestedVersion = Number(queryParams.version) || 1;
  return {
    // FORCE_V4 clamp: legacy (non-4) → 4 only when the flag is ON; otherwise the
    // requested version passes through unchanged (frozen contract).
    version: forceV4 && requestedVersion !== 4 ? 4 : requestedVersion,
    requestedVersion,
    zone,
    smc: queryParams.smc || zone,
    limit: parseInt(queryParams.limit || '10000', 10),
    page: parseInt(queryParams.page || '1', 10),
    perPage: queryParams.perPage,
    next,
    theme: queryParams.theme || 'light',
    mods: (queryParams.mods || '').split(','),
    shared: parseInt(queryParams.futurishared || '0', 10),
    singlePlaylistId: queryParams.id,
    // D-211: the "Full" vs "Single" embed toggle. Read through `pyBool`
    // because POST emits Python's `True` for it on the listen pages, which is
    // the grammar `getNewIframeElement` already accepts on the host page —
    // the two sides of the iframe boundary must agree on the spelling.
    full: pyBool(queryParams.full),
    // D-211 -- the gate's vertical discriminator, read through `pyBool` for the
    // same reason `full` is. Deliberately SEPARATE from parseTheme's `vertical`,
    // which since D-215 reads through `pyBool` TOO -- so the two are now the same
    // value computed independently, and the separation is no longer about
    // grammar. It is about what each one answers: this is a WIRE fact (which of
    // v4's three embed families is this?), parseTheme's is a PRESENTATION one
    // (which layout does it paint?). `showEpisodeList` needs the former, and
    // pointing it at the latter is the coupling D-211's own review caught -- see
    // Player.vue's `showEpisodeList`, and the agreement pin in app-v4-wire.spec.
    verticalEmbed: pyBool(queryParams.vertical),
    displayPlayerInline: next === 'none' || next === 'continue',
    dropToLive: next === 'continue',
  };
};

/**
 * Build the per-station list from the `station` param.
 *
 * Defaults to `wphm` (carrying the shell `zone`) when absent; a single station
 * carries the shell zone; a `station:zone,station:zone` list is split into
 * per-station entries (each with its own zone). Mirrors
 * `createStationInformation` exactly.
 *
 * @param {?Object<string,string>} queryParams parsed query params
 * @param {string} zone the shell zone (fallback for single-station embeds)
 * @returns {Array<{station:string, zone:string}>}
 */
export const parseStations = (queryParams, zone) => {
  const stationlist =
    queryParams && queryParams.station ? queryParams.station : 'wphm';
  if (stationlist.indexOf(',') < 0) {
    return [{ station: stationlist, zone }];
  }
  return stationlist.split(',').map((stationZone) => {
    const [station, stnzone] = stationZone.split(':');
    return { station, zone: stnzone };
  });
};
